import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserSettings, updateUserSettings } from '../services/api';

const SettingsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    notifications: true,
    emailUpdates: true,
    profileVisibility: 'public',
    theme: 'light'
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      if (currentUser) {
        const result = await getUserSettings(currentUser.uid);
        if (result.success) {
          setSettings(result.settings);
        }
      }
    };

    loadSettings();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const result = await updateUserSettings(currentUser.uid, settings);

    if (result.success) {
      setMessage('Settings saved successfully!');
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } else {
      setMessage('Error saving settings. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Account Settings</h1>

          {message && (
            <div className={`mb-4 p-4 rounded ${
              message.includes('success')
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Account Information
              </h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <span className="text-gray-600 ml-2">{currentUser?.email}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Role:</span>
                  <span className="text-gray-600 ml-2 capitalize">
                    {currentUser?.role || 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Notifications
              </h2>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="notifications"
                    checked={settings.notifications}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-gray-700">
                    Enable push notifications
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="emailUpdates"
                    checked={settings.emailUpdates}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-gray-700">
                    Receive email updates about new matches
                  </span>
                </label>
              </div>
            </div>

            {/* Privacy Settings */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Privacy
              </h2>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Profile Visibility
                </label>
                <select
                  name="profileVisibility"
                  value={settings.profileVisibility}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="public">Public - Visible to all employers</option>
                  <option value="private">Private - Only visible when you apply</option>
                  <option value="anonymous">Anonymous - Hide personal details</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Control who can see your profile information
                </p>
              </div>
            </div>

            {/* Appearance Settings */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Appearance
              </h2>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Theme
                </label>
                <select
                  name="theme"
                  value={settings.theme}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto (System Preference)</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Danger Zone */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h2 className="text-xl font-semibold text-red-600 mb-4">
              Danger Zone
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-gray-700 mb-3">
                Delete your account and all associated data. This action cannot be undone.
              </p>
              <button className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700">
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
